import * as React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useAxios } from '../../utils/hooks.ts';
import ProgressWrapper from '../../components/ProgressWrapper';

// Most-to-least privileged, matching co.newlabs.enums.Role's declaration order on the backend -
// /oauth/authorize rejects a requested role more privileged than the caller's own membership,
// so the picker only offers roles from the caller's own role onward in this list.
const ROLE_ORDER = ['OWNER', 'ADMIN', 'USER', 'VIEWER'];

// The consent screen an MCP client (e.g. Claude) redirects the browser to as the
// authorization_endpoint of the OAuth Authorization Code + PKCE flow (see
// co.newlabs.oauth.OAuthDiscoveryController). Login itself is handled for free by the
// app-wide ReactKeycloakProvider login-required guard in App.js - by the time this
// component renders, the user is already authenticated.
export default function Authorize() {
    const axiosInstance = useAxios();
    const toast = React.useRef(null);

    // index.js strips this page's query string into sessionStorage before Keycloak's
    // login-required check ever sees the URL (a long nested redirect_uri broke the round
    // trip through Keycloak - see the comment there), so read it back from there rather
    // than from the URL, which is bare '/authorize' by the time we get here.
    const params = React.useMemo(
        () => new URLSearchParams(window.sessionStorage.getItem('pendingAuthorize') || window.location.search),
        []
    );

    const clientId = params.get('client_id');
    const redirectUri = params.get('redirect_uri');
    const codeChallenge = params.get('code_challenge');
    const codeChallengeMethod = params.get('code_challenge_method');
    const state = params.get('state');
    const resource = params.get('resource');
    const missingParams = !clientId || !redirectUri || !codeChallenge || !codeChallengeMethod;

    const [loading, setLoading] = React.useState(true);
    const [submitting, setSubmitting] = React.useState(false);
    const [projects, setProjects] = React.useState([]);
    const [projectId, setProjectId] = React.useState(null);
    const [role, setRole] = React.useState(null);

    React.useEffect(() => {
        if (missingParams) {
            setLoading(false);
            return;
        }
        axiosInstance.current.get('/projects/all').then((response) => {
            setProjects(response.data);
            if (response.data.length > 0) {
                setProjectId(response.data[0].projectId);
                setRole(response.data[0].role);
            }
        }).catch((err) => {
            console.log(err);
            toast.current.show({ severity: 'error', summary: 'Error!', detail: 'Unable to load your projects' });
        }).finally(() => {
            setLoading(false);
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedProject = projects.find((p) => p.projectId === projectId);
    const roleOptions = selectedProject
        ? ROLE_ORDER.slice(ROLE_ORDER.indexOf(selectedProject.role)).map((r) => ({ name: r, role: r }))
        : [];

    const onProjectChange = (newProjectId) => {
        const project = projects.find((p) => p.projectId === newProjectId);
        setProjectId(newProjectId);
        setRole(project ? project.role : null);
    }

    const redirectWithParams = (extraParams) => {
        const url = new URL(redirectUri);
        Object.entries(extraParams).forEach(([key, value]) => {
            if (value) url.searchParams.set(key, value);
        });
        window.sessionStorage.removeItem('pendingAuthorize');
        window.location.href = url.toString();
    }

    const authorize = () => {
        setSubmitting(true);
        axiosInstance.current.post('/oauth/authorize', {
            projectId, role, clientId, redirectUri, codeChallenge, codeChallengeMethod, resource
        }).then((response) => {
            redirectWithParams({ code: response.data.code, state });
        }).catch((err) => {
            console.log(err);
            toast.current.show({ severity: 'error', summary: 'Error!', detail: 'Unable to authorize this connection' });
            setSubmitting(false);
        })
    }

    const deny = () => {
        redirectWithParams({ error: 'access_denied', state });
    }

    if (loading) {
        return <ProgressWrapper />;
    }

    return (
        <div className="flex-col center mt20">
            <Toast ref={toast} />
            {missingParams ? (
                <div className="card" style={{ maxWidth: 480 }}>
                    <div className="secondary-title">Invalid authorization request</div>
                    <p>This link is missing required parameters. Ask the MCP client to restart the connection.</p>
                </div>
            ) : (
                <div className="card" style={{ maxWidth: 480 }}>
                    <div className="secondary-title">Connect to Featurely</div>
                    <p>An MCP client is requesting access to a Featurely project on your behalf. Choose which project and access level to grant.</p>

                    <div className="field m20">
                        <label htmlFor="project" className="audit-details">Project</label>
                        <Dropdown id="project" optionLabel="name" optionValue="projectId" value={projectId} options={projects} onChange={(e) => onProjectChange(e.value)} disabled={projects.length === 0} />
                    </div>

                    <div className="field m20">
                        <label htmlFor="role" className="audit-details">Access level</label>
                        <Dropdown id="role" optionLabel="name" optionValue="role" value={role} options={roleOptions} onChange={(e) => setRole(e.value)} disabled={!selectedProject} />
                    </div>

                    {projects.length === 0 && <p className="p-error">You don't belong to any projects yet.</p>}

                    <div className="flex-row center mt20">
                        <Button label="Cancel" className="p-button-outlined" onClick={deny} style={{ marginRight: 10 }} disabled={submitting} />
                        <Button label="Authorize" icon="pi pi-check" onClick={authorize} loading={submitting} disabled={!projectId || !role} />
                    </div>
                </div>
            )}
        </div>
    )
}
